import React, { Suspense, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Circle, MoreHorizontal } from 'lucide-react';
import {
  Actions,
  ModelScopeFactory,
  useAction,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import { Icon, Text, Tooltip } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import {
  ResourcePageShell,
  useResourceBreadcrumbs,
} from '../resources/resource-page-shell';
import { useArtifactPanelBackLeading } from '../chat/artifacts/use-artifact-panel-back-leading';
import { useActiveArtifactId } from '@cotera/client/v0/chat/artifact-panel-context';
import {
  DocumentModel,
  DocumentModelProvider,
  useDocumentModel,
} from './document.model';
import { SaveDocumentAction } from './save-document.action';
import {
  ArchiveResourceAction,
  registerArchiveDetailPageBeforeNavigate,
} from '../resources/actions/archive-resource.action';
import { NewChatAction } from '../chat/actions/new-chat.action';
import { useResourceNavigator } from '../resources/navigation';
import { DocumentEditLayout } from './document-edit-layout';
import {
  CommentsRailProvider,
  CommentsRailToggle,
  usePublishCommentTarget,
} from '@cotera/client/app/components/app/rte';
import { useBlockNavigationWithModal } from '@cotera/client/app/hooks/use-history-blocker';
import { ResourceType } from '@cotera/api';
import { FavoriteToggleButton } from '../resources/favorites';
import { ShareResourceLinkAction } from '../resources/actions/share-resource-link.action';
import {
  ResourceSelectionModel,
  ResourceType as RSelectionType,
} from '../resources/selection/resource-selection.model';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  RESOURCE_MEASURE,
  useResourcePageLayout,
} from '../resources/resource-page-framing';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';

const UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes. Are you sure you want to leave?';

const DocumentPageResourceSelectionSync: React.FC<{
  skipNextBlockRef: React.MutableRefObject<boolean>;
}> = ({ skipNextBlockRef }) => {
  const model = useDocumentModel();
  const selection = useInScopeModel(ResourceSelectionModel);
  const actionsContext = useActionsContext();
  const title = useWatchableValue(model.titleWatchable);
  // Use the model's live `isNew` (cleared after the first save) rather than the
  // static router flag, so selection registers once the document is persisted.
  const isNew = useWatchableValue(model.isNewWatchable);

  useEffect(() => {
    registerArchiveDetailPageBeforeNavigate(() => {
      skipNextBlockRef.current = true;
    });
    return () => {
      registerArchiveDetailPageBeforeNavigate(undefined);
    };
  }, [skipNextBlockRef]);

  useEffect(() => {
    if (selection === null || isNew) {
      return;
    }
    const name =
      title !== null && title.trim() !== ''
        ? title.trim()
        : 'Untitled document';
    selection.setSelections([
      {
        resourceType: RSelectionType.Document,
        resourceId: model.documentId,
        name,
      },
    ]);
    actionsContext.targetModel(selection);
  }, [selection, isNew, model.documentId, title, actionsContext]);

  return null;
};

const DocumentPageContent: React.FC<{
  mode: 'artifact' | 'page';
  isNew?: boolean;
  skipNextBlockRef?: React.MutableRefObject<boolean>;
}> = ({ mode, isNew = false, skipNextBlockRef }) => {
  const layout = useResourcePageLayout(mode);
  const location = useLocation();
  const locationState = location.state as DocumentPageLocationState;
  const model = useDocumentModel();
  const org = useWhoami((s) => s.org);
  const title = useWatchableValue(model.titleWatchable);
  const resourceSnapshot = useWatchableValue(model.resourceWatchable);
  const locked = useWatchableValue(model.lockedWatchable);
  // A View grant is enforced by the server on every write; mirroring it here
  // keeps a viewer from typing into an editor whose autosave would then fail.
  const viewOnly = resourceSnapshot?.meta?.permission === 'view';
  const hasUnsavedChanges = useWatchableValue(model.hasUnsavedChangesWatchable);
  const editorInstance = useWatchableValue(model.editorInstanceWatchable);
  const activeArtifactId = useActiveArtifactId();
  const whoami = useWhoami((s) => s.whoami);
  const archiveAction = useAction(ArchiveResourceAction);
  const saveDocumentAction = useAction(SaveDocumentAction);
  const shareAction = useAction(ShareResourceLinkAction);
  const folderIdFromState = locationState?.folderId;
  const folderId = resourceSnapshot?.meta?.folderId ?? null;
  const folderScope = resourceSnapshot?.meta?.folderScope ?? null;
  // Offers this document to the comments rail. On its own page that is the page's own
  // provider; opened as a chat artifact it is the chat's, and only while this is the
  // artifact in front. Either way the rail reads the live editor rather than
  // `editorStateWatchable`, which is written on every keystroke and would re-render
  // this whole header — breadcrumbs, actions, shell — with it.
  usePublishCommentTarget({
    source: mode === 'page' ? 'page' : 'artifact',
    key: model.documentId,
    active: mode === 'page' || activeArtifactId === model.documentId,
    editor: editorInstance,
    authorId: whoami.id,
    authorName: whoami.name ?? whoami.email,
  });

  const shouldBlock = React.useCallback(() => {
    if (skipNextBlockRef?.current === true) {
      skipNextBlockRef.current = false;
      return false;
    }
    return model.hasUnsavedChangesWatchable.snapshot();
  }, [model, skipNextBlockRef]);

  const onSaveAndProceed = React.useCallback(
    async (nextLocation: { pathname: string }) => {
      const isNavigatingToChat = nextLocation.pathname.includes(
        `/${org.slug}/chats/`
      );
      if (!isNavigatingToChat) {
        return false;
      }
      const result = await saveDocumentAction.execute({
        silent: true,
        documentId: model.documentId,
        ...(folderIdFromState !== undefined && { folderId: folderIdFromState }),
      });
      if (result.isErr()) {
        return false;
      }
      if (skipNextBlockRef !== undefined) {
        skipNextBlockRef.current = true;
      }
      return true;
    },
    [folderIdFromState, model, org.slug, saveDocumentAction, skipNextBlockRef]
  );

  useBlockNavigationWithModal(shouldBlock, {
    message: UNSAVED_CHANGES_MESSAGE,
    onSaveAndProceed,
    blockEnabled: hasUnsavedChanges,
  });

  const breadcrumbs = useResourceBreadcrumbs({
    resourceType: ResourceType.Document,
    resourceId: model.documentId,
    resourceName: title,
    // For new (unsaved) documents the resource doesn't exist on the server yet.
    folderId: isNew ? null : folderId,
    folderScope: isNew ? null : folderScope,
  });
  const artifactBackLeading = useArtifactPanelBackLeading();

  const documentActions = (
    <>
      {locked && (
        <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20">
          Editing in progress
        </span>
      )}
      {viewOnly ? (
        <Tooltip
          side="bottom"
          tooltipContent="You can read this document. Ask an editor for edit access."
        >
          <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            View only
          </span>
        </Tooltip>
      ) : (
        <Tooltip
          side="bottom"
          tooltipContent={
            hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'
          }
        >
          <span className="shrink-0">
            {hasUnsavedChanges ? (
              <Circle className="h-2 w-2 fill-amber-500 text-amber-500" />
            ) : (
              <CheckCircle className="h-4 w-4 text-green-600" />
            )}
          </span>
        </Tooltip>
      )}
      {/* In whichever bar this document's actions land in: its own page header, or
          the artifact panel's bar when it is opened from a chat. Comments belong to
          the thing being read, not to the conversation around it — in the chat's
          header this sat beside Share, where it read as something about the chat and
          went stale the moment you switched artifact tabs. */}
      <CommentsRailToggle />
      {mode === 'page' && !isNew && !locked && (
        <>
          {/* `outline` for the same reason as the chat header: the page's
              primary action is the document body, so no header button earns a
              filled treatment. Matching the two keeps one rule across the app
              rather than two presentations of the same action. */}
          <Button
            variant="outline"
            size="sm"
            aria-label="Share document"
            className="ml-2"
            onClick={() => {
              if (resourceSnapshot == null) {
                return;
              }
              void shareAction.execute({
                targets: [
                  {
                    type: ResourceType.Document,
                    id: resourceSnapshot.document.id,
                  },
                ],
              });
            }}
          >
            <Icon icon="share" />
            Share
          </Button>
          {!viewOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Document actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="bottom">
                <DropdownMenuItem
                  onSelect={() =>
                    void saveDocumentAction.execute({
                      documentId: model.documentId,
                      ...(folderIdFromState !== undefined && {
                        folderId: folderIdFromState,
                      }),
                    })
                  }
                >
                  <Icon icon="save" />
                  Save
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => void archiveAction.execute({})}
                >
                  <Icon icon="archive-box" />
                  Archive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      )}
    </>
  );

  if (resourceSnapshot == null) {
    return <DocumentPageSkeleton mode={mode} />;
  }

  // Only on its own page. As an artifact the document publishes into the chat's
  // provider instead, and a provider here would intercept it.
  const withCommentsRail = (page: React.ReactElement): React.ReactElement =>
    mode === 'page' ? (
      <CommentsRailProvider accepts="page">{page}</CommentsRailProvider>
    ) : (
      page
    );

  return withCommentsRail(
    <ResourcePageShell
      breadcrumbs={breadcrumbs}
      leading={artifactBackLeading}
      // Beside the name rather than in the actions cluster; see the agent page.
      // Only for a document that exists: `isNew` means nothing has been saved
      // yet, so there is no id for a favorite to point at.
      titleAdornment={
        isNew ? undefined : (
          <FavoriteToggleButton
            type={ResourceType.Document}
            id={model.documentId}
            className="h-6 w-6"
            iconClassName="h-3.5 w-3.5"
          />
        )
      }
      actions={documentActions}
      layout={layout}
      surface="editor"
    >
      <DocumentEditLayout
        skipNextBlockRef={skipNextBlockRef}
        disabled={locked || viewOnly}
      />
    </ResourcePageShell>
  );
};

type DocumentPageLocationState = { new?: boolean; folderId?: string } | null;

const DocumentPageSkeleton: React.FC<{ mode?: 'artifact' | 'page' }> = ({
  mode = 'page',
}) => {
  const layout = useResourcePageLayout(mode);
  return (
    // Render through the same shell (and layout) as the loaded page so the rounded
    // content panel and breadcrumb bar are already in place while loading. Without
    // this, the bare full-bleed skeleton flashes the unrounded layout before the
    // content mounts inside the shell.
    <ResourcePageShell breadcrumbs={undefined} layout={layout} surface="editor">
      <div className="p-5">
        <div className={cn('space-y-4', RESOURCE_MEASURE)}>
          <div className="h-10 w-2/3 rounded bg-coco-accent animate-pulse" />
          <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
          <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
          <div className="h-4 w-4/5 rounded bg-coco-accent animate-pulse" />
        </div>
      </div>
    </ResourcePageShell>
  );
};

const DocumentPageWithModelData: React.FC<{
  mode: 'artifact' | 'page';
  skipNextBlockRef: React.MutableRefObject<boolean>;
}> = ({ mode, skipNextBlockRef }) => {
  const model = useDocumentModel();
  const status = useWatchableValue(model.loadStatusWatchable);
  const resource = useWatchableValue(model.resourceWatchable);

  if (status === 'not-found') {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <Text.P className="text-muted-foreground">Document not found</Text.P>
      </div>
    );
  }
  if (status === 'loading' || resource === null) {
    return <DocumentPageSkeleton mode={mode} />;
  }

  return (
    <DocumentPageContent
      mode={mode}
      isNew={model.isNew}
      skipNextBlockRef={skipNextBlockRef}
    />
  );
};

const DocumentPageWithModelScope: React.FC<{
  id: string;
  mode: 'artifact' | 'page';
  isNew: boolean;
  /** Set when rendered as an artifact, where there is no router state to read. */
  folderId?: string;
}> = ({ id, mode, isNew, folderId }) => {
  const location = useLocation();
  const locationState = location.state as DocumentPageLocationState;
  const folderIdFromState = folderId ?? locationState?.folderId;
  const whoami = useWhoami((s) => s.whoami);
  const org = useWhoami((s) => s.org);
  if (whoami === undefined || org === undefined) {
    return <DocumentPageSkeleton mode={mode} />;
  }

  return (
    <DocumentPageWithModelScopeReady
      id={id}
      mode={mode}
      isNew={isNew}
      folderIdFromState={folderIdFromState}
      userId={whoami.id}
      orgId={org.id}
      orgSlug={org.slug}
    />
  );
};

const DocumentPageWithModelScopeReady: React.FC<{
  id: string;
  mode: 'artifact' | 'page';
  isNew: boolean;
  folderIdFromState?: string;
  userId: string;
  orgId: string;
  orgSlug: string;
}> = ({ id, mode, isNew, folderIdFromState, userId, orgId, orgSlug }) => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const watchablesFactory = useWatchablesFactory();
  const navigate = useNavigate();
  // Surface-aware: this page can itself be rendered inside an artifact panel,
  // where the actions model scope is isolated and the fallback is what runs.
  const resourceNavigator = useResourceNavigator();
  const skipNextBlockRef = React.useRef(false);

  const actions = React.useMemo(
    () => [
      new SaveDocumentAction(
        client.resource.document,
        queryClient,
        navigate,
        orgSlug,
        folderIdFromState
      ),
      new NewChatAction(resourceNavigator, client.resource.dataset),
    ],
    [
      client.resource.document,
      client.resource.dataset,
      queryClient,
      navigate,
      orgSlug,
      folderIdFromState,
      resourceNavigator,
    ]
  );

  const createModels = useCallback(
    () => [
      new DocumentModel({
        documentId: id,
        orgId,
        userId,
        isNew,
        mode,
        client,
        watchablesFactory,
      }),
      new ResourceSelectionModel(),
    ],
    [id, orgId, userId, isNew, mode, client, watchablesFactory]
  );

  const activeArtifactId = useActiveArtifactId();
  const target = activeArtifactId === null || activeArtifactId === id;

  return (
    <Actions actions={actions} key={id}>
      <ModelScopeFactory
        createModels={createModels}
        deps={[id, orgId, userId, isNew, mode]}
      >
        <DocumentModelProvider modelId={id} target={target}>
          <DocumentPageResourceSelectionSync
            skipNextBlockRef={skipNextBlockRef}
          />
          <DocumentPageWithModelData
            mode={mode}
            skipNextBlockRef={skipNextBlockRef}
          />
        </DocumentModelProvider>
      </ModelScopeFactory>
    </Actions>
  );
};

export const DocumentPageWithFetchedData: React.FC<{
  id: string;
  mode: 'artifact' | 'page';
  /** True for a document that doesn't exist server-side yet ("New document"). */
  isNew?: boolean;
  folderId?: string;
}> = ({ id, mode, isNew = false, folderId }) => {
  return (
    <DocumentPageWithModelScope
      id={id}
      mode={mode}
      isNew={isNew}
      folderId={folderId}
    />
  );
};

export const DocumentPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const location = useLocation();
  const locationState = location.state as DocumentPageLocationState;
  const isNewFromState = locationState?.new === true;

  useTracking(
    CocoEvent.DocumentViewed,
    { documentId: documentId ?? null },
    { enabled: documentId !== undefined }
  );

  if (documentId === undefined) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <Text.P className="text-muted-foreground">Invalid document</Text.P>
      </div>
    );
  }

  return (
    <Suspense fallback={<DocumentPageSkeleton />}>
      <DocumentPageWithModelScope
        id={documentId}
        mode="page"
        isNew={isNewFromState}
      />
    </Suspense>
  );
};
